import {
  CalendarClock,
  Download,
  ExternalLink,
  Globe,
  Library,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toolkitApi } from "@/api/toolkit";
import { PERMISSIONS } from "@/auth/permissions";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Select,
  Spinner,
} from "@/components/common";
import { useHasPermission } from "@/hooks/usePermission";
import {
  useOwuDocuments,
  useOwuStatus,
  useStartOwuScan,
} from "@/hooks/useToolkit";
import type { OwuDocument, OwuScanRun } from "@/types/toolkit";
import { formatDate, formatFileSize } from "./format";
import { DeleteOwuModal, OwuSourcesModal, UploadOwuModal } from "./OwuModals";

const ALL = "";

async function download(document: OwuDocument) {
  try {
    const blob = await toolkitApi.downloadOwu(document.id);
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = document.fileName;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(
      (error as { message?: string }).message ?? "Nie udało się pobrać OWU",
    );
  }
}

function hostOf(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function LastRun({ run }: { run: OwuScanRun }) {
  if (!run.finishedAt) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Spinner size="sm" /> Trwa sprawdzanie stron towarzystw…
      </span>
    );
  }
  return (
    <span>
      Ostatnio sprawdzono {formatDate(run.finishedAt)}
      {run.startedManually ? " (ręcznie)" : ""}: {run.insurersChecked}{" "}
      towarzystw, nowych dokumentów: {run.documentsAdded}.
    </span>
  );
}

export function OwuLibraryTab() {
  const { data: status } = useOwuStatus();
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useOwuDocuments(showArchived);
  const canCreate = useHasPermission(PERMISSIONS.TOOLKIT.CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.TOOLKIT.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.TOOLKIT.DELETE);
  const scan = useStartOwuScan();
  const [query, setQuery] = useState("");
  const [product, setProduct] = useState(ALL);
  const [insurer, setInsurer] = useState(ALL);
  const [uploading, setUploading] = useState(false);
  const [editingSources, setEditingSources] = useState(false);
  const [deleting, setDeleting] = useState<OwuDocument | null>(null);

  const running = !!status?.lastRun && !status.lastRun.finishedAt;
  const products = status?.products ?? [];

  const insurerOptions = useMemo(
    () =>
      [...new Map((data ?? []).map((d) => [d.insurerId, d.insurerName]))]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, "pl")),
    [data],
  );

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byInsurer = new Map<string, OwuDocument[]>();
    for (const d of data ?? []) {
      if (product && d.product !== product) {
        continue;
      }
      if (insurer && d.insurerId !== insurer) {
        continue;
      }
      if (
        q &&
        !`${d.name} ${d.insurerName} ${d.versionLabel ?? ""}`
          .toLowerCase()
          .includes(q)
      ) {
        continue;
      }
      byInsurer.set(d.insurerName, [
        ...(byInsurer.get(d.insurerName) ?? []),
        d,
      ]);
    }
    return [...byInsurer.entries()].sort(([a], [b]) =>
      a.localeCompare(b, "pl"),
    );
  }, [data, query, product, insurer]);

  return (
    <div className="space-y-4">
      <Card padding="none">
        <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 gap-3 text-sm text-sub">
            <CalendarClock
              size={18}
              aria-hidden
              className="mt-0.5 shrink-0 text-adara-600"
            />
            <div className="min-w-0 space-y-0.5">
              <p>
                Raz w tygodniu panel sprawdza oficjalne strony towarzystw i
                dodaje nowe wersje OWU. Starsze wersje zostają w archiwum.
              </p>
              <p className="text-xs text-dim">
                {status?.lastRun ? (
                  <LastRun run={status.lastRun} />
                ) : (
                  "Jeszcze nie sprawdzano."
                )}
                {status &&
                  !running &&
                  ` Następne automatyczne sprawdzenie: ${formatDate(status.nextScheduledRun)}.`}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {canUpdate && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Globe size={14} aria-hidden />}
                onClick={() => {
                  setEditingSources(true);
                }}
              >
                Domeny
              </Button>
            )}
            {canCreate && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Upload size={14} aria-hidden />}
                onClick={() => {
                  setUploading(true);
                }}
              >
                Dodaj ręcznie
              </Button>
            )}
            {canUpdate && (
              <Button
                size="sm"
                leftIcon={<RefreshCw size={14} aria-hidden />}
                disabled={running}
                isLoading={scan.isPending}
                onClick={() => {
                  scan.mutate(undefined, {
                    onSuccess: () => {
                      toast.success(
                        "Sprawdzanie ruszyło — potrwa kilka minut. Nowe OWU pojawią się na liście.",
                      );
                    },
                    onError: (error) => {
                      toast.error(
                        (error as { message?: string }).message ??
                          "Nie udało się uruchomić sprawdzania",
                      );
                    },
                  });
                }}
              >
                Sprawdź teraz
              </Button>
            )}
          </div>
        </div>
        {status?.lastRun?.summary && canUpdate && (
          <details className="border-t border-edge-soft px-4 py-2 text-xs text-dim">
            <summary className="cursor-pointer">
              Uwagi z ostatniego sprawdzenia
            </summary>
            <p className="mt-1 whitespace-pre-line">{status.lastRun.summary}</p>
          </details>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-center">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder="Szukaj OWU…"
          leftIcon={<Search size={16} aria-hidden />}
          aria-label="Szukaj w bibliotece OWU"
        />
        <Select
          options={[
            { value: ALL, label: "Wszystkie produkty" },
            ...products.map((p) => ({ value: p, label: p })),
          ]}
          value={product}
          onChange={setProduct}
          className="sm:w-48"
        />
        <Select
          options={[
            { value: ALL, label: "Wszystkie towarzystwa" },
            ...insurerOptions,
          ]}
          value={insurer}
          onChange={setInsurer}
          className="sm:w-56"
        />
      </div>
      <Checkbox
        label="Pokaż archiwalne wersje"
        checked={showArchived}
        onChange={(e) => {
          setShowArchived(e.target.checked);
        }}
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Library size={24} aria-hidden />}
            title={data?.length ? "Brak wyników" : "Biblioteka OWU jest pusta"}
            description={
              data?.length
                ? "Nic nie pasuje do wyszukiwania."
                : "OWU pojawią się tutaj po pierwszym sprawdzeniu stron towarzystw albo po dodaniu ich ręcznie."
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map(([insurerName, documents]) => (
            <section key={insurerName} className="space-y-2">
              <h2 className="text-xs font-semibold text-dim">
                {insurerName}
              </h2>
              <Card padding="none">
                <ul className="divide-y divide-edge-soft">
                  {documents.map((d) => (
                    <li
                      key={d.id}
                      className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center ${d.isCurrent ? "" : "opacity-70"}`}
                    >
                      <div className="flex min-w-0 flex-1 gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-[10px] font-bold text-dim">
                          PDF
                        </div>
                        <div className="min-w-0 space-y-0.5">
                          <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-main">
                            {d.name}
                            <Badge variant="brand">{d.product}</Badge>
                            {!d.isCurrent && <Badge>archiwalna</Badge>}
                          </p>
                          <p className="text-sm text-dim">
                            {[
                              d.versionLabel && `wersja ${d.versionLabel}`,
                              d.validFrom &&
                                `obowiązuje od ${formatDate(d.validFrom)}`,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "bez oznaczenia wersji"}
                          </p>
                          <p className="text-xs text-faint">
                            {formatFileSize(d.sizeBytes)} ·{" "}
                            {d.addedManually ? "dodano ręcznie" : "pobrano"}{" "}
                            {formatDate(d.retrievedAt)}
                            {d.sourceUrl && (
                              <>
                                {" z "}
                                <a
                                  href={d.sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-0.5 text-adara-700 hover:underline dark:text-adara-300"
                                >
                                  {hostOf(d.sourceUrl)}
                                  <ExternalLink size={11} aria-hidden />
                                </a>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1 self-end sm:self-auto">
                        {canDelete && (
                          <button
                            type="button"
                            aria-label={`Usuń ${d.name}`}
                            title={`Usuń ${d.name}`}
                            onClick={() => {
                              setDeleting(d);
                            }}
                            className="rounded-md p-1.5 text-faint hover:bg-muted hover:text-main focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adara-500"
                          >
                            <Trash2 size={16} aria-hidden />
                          </button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<Download size={14} aria-hidden />}
                          onClick={() => {
                            void download(d);
                          }}
                        >
                          Pobierz
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}

      <UploadOwuModal
        isOpen={uploading}
        products={products}
        onClose={() => {
          setUploading(false);
        }}
      />
      <OwuSourcesModal
        isOpen={editingSources}
        onClose={() => {
          setEditingSources(false);
        }}
      />
      <DeleteOwuModal
        document={deleting}
        onClose={() => {
          setDeleting(null);
        }}
      />
    </div>
  );
}
